/**
 * What the customer is asked to pay, and when.
 *
 * Three moments, in the order they happen to a real job:
 *
 *   Signed          Mercury asks for the first payment.
 *   Sends, or the   QuickBooks opens an invoice for the whole contract,
 *   first payment   whichever of the two comes first.
 *   Money lands     Roof Board records it, and so does QuickBooks.
 *
 * The gap between the first two is deliberate, and Jonathon set it on
 * 2026-08-26: "I don't want QuickBooks to create an invoice just because
 * someone signs because they can back out or it could have been an error."
 * A signature is enough to ask for a deposit — the customer can always be
 * refunded — but not enough to book revenue. See docs/adr/0043.
 *
 * This module is the mapping and nothing else: no network, no token, no
 * Supabase. It turns rows this application already holds into the shapes
 * Mercury's Accounts Receivable API accepts, and it is the only place a payment
 * amount crosses that boundary. The Edge Function transmits what these
 * functions produce and never works an amount out for itself, for the same
 * reason `quickbooks.ts` does not: a second copy of the payment rules, in Deno,
 * out of reach of this package's tests, is drift waiting to happen.
 *
 * Money arrives as integer cents (CLAUDE.md rule 3) and leaves as the decimal
 * Mercury insists on, in one function, once.
 */

import { type Cents, toDecimal } from "./money.js";
import { PAYMENT_SLOT_LABEL } from "./constants.js";
import { CARD_SURCHARGE_LABEL } from "./paymentChoice.js";
import {
  type StoredPayment,
  paymentSchedule,
  PAYMENT_SPLIT_THRESHOLD,
} from "./paymentSchedule.js";

/** Mercury's live API. There is no sandbox: a token is real or it is nothing. */
export const MERCURY_API_BASE = "https://api.mercury.com/api/v1";

/**
 * The amount, as Mercury wants it: a decimal with at most two places.
 *
 * Same reasoning as `qboAmount`. `toDecimal` divides integer cents by 100, so
 * 1_089_81 becomes 1089.81 exactly, where a float assembled out of dollars
 * drifts a cent and leaves somebody reconciling by hand.
 */
export function mercuryAmount(value: Cents): number {
  return toDecimal(value);
}

/**
 * Which payment slots Mercury sends by itself, and which wait for a person.
 *
 * Slot 1 goes automatically the moment the contract is signed — that is the
 * whole point, and Jonathon asked for it in those words ("send automatically").
 * Slots 2 and 3 do not: they exist for the jobs where a rep has typed a
 * staged schedule, and what releases them is work actually being finished, not
 * a date. They surface as prompts on the production board instead
 * ("2 and 3 can be prompts in Roof Board to the project manager and sales rep
 * based on work orders getting completed").
 *
 * The Final slot is never requested here. It is collected on completion, which
 * is a different trigger and a different conversation.
 */
export const AUTOMATIC_SLOT = 1;
export const PROMPTED_SLOTS = [2, 3] as const;

/**
 * The payment to ask for at signing, or null when there is none to ask for.
 *
 * Null is the ordinary answer on a small job, not a failure: under
 * $1,000 the whole contract is due on completion and there is no deposit
 * (`paymentSchedule`). Returning null rather than zero keeps "nothing is owed
 * yet" distinct from "zero dollars are owed", which is the distinction the
 * queue needs in order to not send a $0.00 invoice to a customer.
 */
export function depositDue(
  total: Cents,
  payments: readonly StoredPayment[],
): Cents | null {
  const schedule = paymentSchedule(total, payments);
  // An errored schedule does not add up to the contract. Asking a customer for
  // money out of a schedule we know to be wrong is worse than asking late.
  if (schedule.error) return null;
  if (!schedule.split) return null;
  const first = schedule.rows.find((row) => row.slot === AUTOMATIC_SLOT);
  return first && first.amount > 0 ? first.amount : null;
}

/**
 * The amount for any slot, for the prompted ones and for tests.
 *
 * Same guard as above: a schedule carrying an error yields nothing at all.
 */
export function slotDue(
  total: Cents,
  payments: readonly StoredPayment[],
  slot: number,
): Cents | null {
  const schedule = paymentSchedule(total, payments);
  if (schedule.error) return null;
  const row = schedule.rows.find((r) => r.slot === slot);
  return row && row.amount > 0 ? row.amount : null;
}

/*
 * The card ceiling is gone. It was `DEFAULT_CARD_LIMIT` ($5,000) and
 * `cardsAllowed()`, set by Jonathon on 2026-08-27 — "cards or ach for jobs 5k
 * or less" — and removed by him on 2026-09-06.
 *
 * It only ever existed because the company paid Stripe's 2.9% + 30c: $14.80 on
 * a $500 repair was worth it for money that cleared the same day, $522 on an
 * $18,000 roof was not. The customer now pays 3% themselves when they choose a
 * card (`paymentChoice.ts`), so there is nothing left to protect and a card is
 * offered on a job of any size. `mercury_settings.card_limit_cents` stays in
 * the database because an applied migration is never edited, and nothing reads
 * it.
 */

/**
 * What a card would cost the company on this amount, in cents.
 *
 * Stripe's published rate, stated here so the Integrations screen can show a
 * real number rather than a percentage the reader has to do arithmetic on. It
 * is a display aid and nothing decides anything by it.
 */
export const CARD_FEE_RATE = 0.029;
export const CARD_FEE_FIXED = 30 as Cents;

export function cardFee(amount: Cents): Cents {
  if (amount <= 0) return 0 as Cents;
  return (Math.round(amount * CARD_FEE_RATE) + CARD_FEE_FIXED) as Cents;
}

/** The parts of a contact Mercury needs to bill somebody. */
export interface PayingContact {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  email?: string | null;
}

/** The parts of a job that name what the payment is for. */
export interface PayableJob {
  job_number?: number | string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
}

/**
 * The customer's name, as Mercury files it.
 *
 * Deliberately plainer than `quickbooksDisplayName`: Mercury has no
 * sub-customer syntax, so a colon is just a colon and needs no escaping. The
 * fallback chain is the same one the database trigger uses to derive
 * `full_name` (migration 20260813100000), so the two never disagree about who
 * a contact is.
 */
export function payerName(contact: PayingContact): string {
  const parts = [contact.first_name, contact.last_name]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return (
    (contact.full_name ?? "").trim() ||
    parts ||
    (contact.company ?? "").trim() ||
    ""
  );
}

/** A Mercury AR customer, as `POST /ar/customers` takes it. */
export interface MercuryCustomer {
  name: string;
  email: string;
}

/**
 * A contact, as a Mercury customer.
 *
 * `email` is one string, not a list. This was written as `emails: string[]`
 * and Mercury answered every send with a bare `400` — the first real request
 * ever made, on 2026-08-30, failed here and got no further. Mercury documents
 * `name` and `email`, both required, both singular.
 *
 * It must not be blank: an invoice with nowhere to go is refused, and rightly.
 * `paymentBlockers` catches that before we get here, so a contact without an
 * email never reaches this function.
 */
export function mercuryCustomer(contact: PayingContact): MercuryCustomer {
  return {
    name: payerName(contact),
    email: (contact.email ?? "").trim(),
  };
}

/**
 * One line on a Mercury invoice.
 *
 * The text field is `name`. It was written as `description`, which Mercury
 * refuses — see mercuryCustomer above for the sibling mistake that hid this
 * one by failing first.
 */
export interface MercuryLineItem {
  name: string;
  quantity: number;
  unitPrice: number;
}

/** A Mercury invoice, as `POST /ar/invoices` takes it. */
export interface MercuryInvoice {
  customerId: string;
  destinationAccountId: string;
  invoiceDate: string;
  dueDate: string;
  lineItems: MercuryLineItem[];
  creditCardEnabled: boolean;
  achDebitEnabled: boolean;
  useRealAccountNumber: boolean;
  invoiceNumber?: string;
  payerMemo?: string;
  internalNote?: string;
  ccEmails: string[];
}

/**
 * What this payment is for, in a form still legible in a bank feed a year on.
 *
 * The service address, not the customer's mailing address — a landlord with
 * four properties gets four payment requests that only the address tells apart,
 * and the job carries its own address for exactly that reason (ADR 0003).
 */
export function paymentDescription(
  job: PayableJob,
  slot: number,
  total?: Cents | null,
): string {
  const street = (job.address ?? "").trim();
  const city = (job.city ?? "").trim();
  const state = (job.state ?? "").trim();
  const where = [street, [city, state].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" — ");
  const label = PAYMENT_SLOT_LABEL[slot] ?? "Payment";
  const of =
    total != null && total > 0
      ? ` of the ${formatContract(total)} contract`
      : "";
  return [`${label}${of}`, where].filter(Boolean).join(" · ");
}

/** Dollars with no cents when it is a round number, for prose like the above. */
function formatContract(total: Cents): string {
  const dollars = toDecimal(total);
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString("en-US")}`
    : `$${dollars.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}

/**
 * How long the customer has to pay a deposit.
 *
 * Zero days — due on receipt. A deposit is what releases the job to
 * production, so a due date a fortnight out would say the opposite of what the
 * company means by asking for it. Mercury requires the field, so it is stated
 * rather than left to their default.
 */
export const DEPOSIT_DUE_DAYS = 0;

/** `YYYY-MM-DD`, `days` after `from`, staying a plain calendar date. */
export function dueDate(from: string, days: number): string {
  const at = new Date(`${from}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/**
 * The invoice number Roof Board stamps on a payment request.
 *
 * The job number for the first payment, and the job number with the slot after
 * it for the rest: `1051`, `1051-2`. Exported because the matching in
 * depositLink.ts has to recognise the same string on a bank line, and a second
 * copy of this rule in the app is a rule that drifts (Josh, 2026-09-13).
 */
export function invoiceNumberFor(
  jobNumber: string | number | null | undefined,
  slot: number | null | undefined,
  /**
   * A link somebody raised by hand is `1051-M1` (ADR 0141): its own series,
   * kept apart from the scheduled `1051` and `1051-2` so a bank line naming
   * one can never be read as the other.
   */
  options: { manualReference?: string | number | null } = {},
): string | null {
  if (jobNumber == null) return null;
  if (options.manualReference != null && options.manualReference !== "") {
    return `${jobNumber}-M${options.manualReference}`;
  }
  if (slot == null) return null;
  return slot === AUTOMATIC_SLOT ? String(jobNumber) : `${jobNumber}-${slot}`;
}

/**
 * The number a payment's invoice is raised under the `reissue`th time.
 *
 * Mercury never takes an invoice number twice, a cancelled invoice's included.
 * So when a customer changes how they pay and the first invoice is cancelled,
 * the second cannot be `1119` again: it is `1119-R2`, then `1119-R3`. The
 * first is the plain number from `invoiceNumberFor`. `-R` keeps the series
 * apart from `1119-2`, the second scheduled payment, and `1119-M1`, a link
 * raised by hand, so a bank line naming one is never read as another.
 */
export function reissuedInvoiceNumber(base: string, reissue: number): string {
  return reissue <= 1 ? base : `${base}-R${reissue}`;
}

/** `base`, escaped, followed by an optional `-R<n>`: the whole series. */
export function invoiceSeriesPattern(base: string): string {
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return `${escaped}(?:-R(\\d+))?`;
}

/**
 * Which issue of `base` an invoice number is: 1 for `base` itself, 2 for
 * `base-R2`, and null for anything outside the series, `base-2` included.
 */
export function reissueOf(base: string, number: string): number | null {
  if (base.trim() === "") return null;
  const found = new RegExp(`^${invoiceSeriesPattern(base)}$`).exec(
    number.trim(),
  );
  if (!found) return null;
  return found[1] ? Number(found[1]) : 1;
}

/**
 * The payment request itself.
 *
 * From 2026-09-06 an invoice is raised for **one method**, because the customer
 * has already chosen it on the page after signing, and the amount depends on
 * which: a card invoice carries a second line for the 3% and a bank transfer
 * invoice does not. An invoice offering both could only name one total, which
 * is the reason a surcharge was thought impossible in ADR 0063 — asking first
 * is what makes it possible.
 *
 * Three flags Mercury requires, each a decision rather than a default:
 *
 * - **`creditCardEnabled`** is true only on a card invoice, and only ever when
 *   Stripe is actually connected to the Mercury account. Mercury validates this
 *   and refuses the whole invoice otherwise, so a wrong `true` here is not a
 *   card option that quietly fails — it is a deposit that never gets asked for.
 * - **`achDebitEnabled`** is true only on a bank transfer invoice. It costs the
 *   company nothing, which is why it is the one offered without a fee.
 * - **`useRealAccountNumber`** is off. Mercury issues a virtual account number
 *   for the invoice instead of printing the company's real one on a document
 *   that goes to a stranger's inbox.
 */
export function mercuryInvoice(input: {
  /** Mercury's id for the customer, already created or looked up. */
  customerId: string;
  /** The Mercury account the money lands in. */
  destinationAccountId: string;
  amount: Cents;
  job: PayableJob;
  slot: number;
  /** The whole contract, so the line can say what this is a slice of. */
  contractTotal?: Cents | null;
  /** `YYYY-MM-DD`. The day the contract was signed, not the day this ran. */
  invoiceDate: string;
  /** Which one the customer picked on the page. */
  method: "card" | "ach";
  /**
   * The 3% on a card payment, as its own line.
   *
   * Passed in rather than worked out here so that the figure on the invoice is
   * the same object the page quoted and the estimate records — `chargeFor` in
   * paymentChoice.ts is the only thing that multiplies by 0.03.
   */
  surcharge?: Cents | null;
  ccEmails?: readonly string[];
  /**
   * A payment that is not one of the four on the schedule (ADR 0141).
   *
   * `label` is what the rep said it is for, and it replaces the "First Payment
   * of the $18,000 contract" line — a customer being asked for a change order
   * should read about the change order. `reference` keeps the invoice number
   * apart from the scheduled ones on the same job, so `1051` and `1051-M1` can
   * never be confused for each other when a bank line names one.
   */
  manual?: { label: string; reference: string } | null;
}): MercuryInvoice {
  const description = input.manual
    ? input.manual.label
    : paymentDescription(input.job, input.slot, input.contractTotal);
  const invoice: MercuryInvoice = {
    customerId: input.customerId,
    destinationAccountId: input.destinationAccountId,
    invoiceDate: input.invoiceDate,
    dueDate: dueDate(input.invoiceDate, DEPOSIT_DUE_DAYS),
    lineItems: [
      {
        name: description,
        quantity: 1,
        unitPrice: mercuryAmount(input.amount),
      },
      // The fee is a line the customer can read, not arithmetic folded into the
      // amount. A total 3% larger than the contract said, with nothing on the
      // invoice explaining it, is the version of this that generates a phone
      // call — and, on a card, a chargeback.
      ...(input.method === "card" && (input.surcharge ?? 0) > 0
        ? [
            {
              name: CARD_SURCHARGE_LABEL,
              quantity: 1,
              unitPrice: mercuryAmount(input.surcharge as Cents),
            },
          ]
        : []),
    ],
    creditCardEnabled: input.method === "card",
    achDebitEnabled: input.method === "ach",
    useRealAccountNumber: false,
    ccEmails: [...(input.ccEmails ?? [])],
    payerMemo: description,
  };

  // Mercury derives an invoice number from the customer's last one when none is
  // given, which would number a landlord's four properties as one sequence.
  // The job number is the number everybody here already says out loud.
  const number = invoiceNumberFor(input.job.job_number, input.slot, {
    manualReference: input.manual?.reference ?? null,
  });
  if (number) invoice.invoiceNumber = number;

  return invoice;
}

/**
 * Why this payment cannot be requested, in words a person can act on.
 *
 * Returns an empty array when it can. Nothing here blocks a signature — the
 * contract is already signed by the time this runs — it decides whether a row
 * waits in the queue with a reason attached instead of failing against the API
 * and coming back as whatever Mercury's validator says.
 */
export function paymentBlockers(input: {
  amount?: Cents | null;
  contact?: PayingContact | null;
  destinationAccountId?: string | null;
}): string[] {
  const blockers: string[] = [];

  if (input.amount == null) {
    blockers.push("No deposit has been worked out for this job yet.");
  } else if (input.amount <= 0) {
    blockers.push("The deposit is zero, so there is nothing to ask for.");
  }

  if (!input.contact || !payerName(input.contact)) {
    blockers.push("The customer has no name to address the request to.");
  } else if (!(input.contact.email ?? "").trim()) {
    blockers.push(
      `${payerName(input.contact)} has no email address, and Mercury ` +
        "sends the payment request by email. Add one to the contact.",
    );
  }

  if (!(input.destinationAccountId ?? "").trim()) {
    blockers.push(
      "No Mercury account has been chosen for deposits to land in. " +
        "Pick one in Tools → Integrations.",
    );
  }

  return blockers;
}

/** The states a queued payment request can be in. Mirrors the database CHECK. */
export const MERCURY_REQUEST_STATES = [
  "pending",
  "sent",
  "paid",
  "failed",
  "cancelled",
] as const;

export type MercuryRequestState = (typeof MERCURY_REQUEST_STATES)[number];

/**
 * Mercury's own invoice statuses, mapped onto ours.
 *
 * Their vocabulary is larger than what this application needs to act on, and
 * it will grow. Anything unrecognised stays `sent` rather than becoming
 * `failed`: a status we have not seen before is not evidence that the customer
 * failed to pay, and marking it so would take a live request off the board.
 */
export function stateFromMercuryStatus(
  status: string | null | undefined,
): MercuryRequestState {
  switch ((status ?? "").trim().toLowerCase()) {
    case "paid":
    case "complete":
    case "completed":
      return "paid";
    case "cancelled":
    case "canceled":
    case "void":
    case "voided":
      return "cancelled";
    default:
      return "sent";
  }
}

/**
 * Whether Mercury's status means the customer has paid and the money is
 * clearing. Mercury calls it `Processing`; for an ACH debit it lasts one to
 * five business days. Not paid — `stateFromMercuryStatus` still says `sent`
 * and the poll keeps asking — but not to be chased either.
 */
export function paymentStarted(status: string | null | undefined): boolean {
  return (status ?? "").trim().toLowerCase() === "processing";
}

/**
 * What to say about a queued payment request on screen.
 *
 * `failed` carries Mercury's own words when there are any. The alternative —
 * a generic "could not send" — is what makes an integration feel haunted: the
 * thing that knows why says nothing, and somebody re-presses a button for an
 * hour. The same reasoning as `quickbooksInvoiceNotice`.
 */
export function mercuryRequestNotice(
  state: MercuryRequestState,
  error?: string | null,
): string {
  switch (state) {
    case "pending":
      return error?.trim()
        ? `Waiting, but it cannot be sent yet: ${error.trim()}`
        : "Waiting to be sent to the customer.";
    case "sent":
      return "Sent to the customer. Waiting for payment.";
    case "paid":
      return "Paid.";
    case "failed":
      return error?.trim()
        ? `Mercury refused it: ${error.trim()}`
        : "Mercury refused it.";
    case "cancelled":
      return "Cancelled in Mercury.";
  }
}

/**
 * Whether a signed job should be asked for a deposit at all.
 *
 * Split out from `depositDue` so the trigger side and the sending side agree
 * without one importing the other's reasoning. A job under the threshold is
 * not a job with a $0 deposit — it is a job with no deposit step.
 */
export function asksForADeposit(total: Cents): boolean {
  return total >= PAYMENT_SPLIT_THRESHOLD;
}
